import React, { useState, useMemo } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import EventList from '../../../components/lpr/EventList';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { LPRParamList } from '../../../typings/Params';
import LprSearchFilter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

interface SearchParams {
    navigation: Navigation;
    authenticatedUser?: AuthenticatedUser;
}

const limit = 100;

export default function Search({ navigation }: SearchParams) {

    const route = useRoute<RouteProp<LPRParamList, 'LprSearch'>>();
    const [filters, setFilters] = useFilters<LPRParamList['LprSearch']>({
        ...route.params,
    });

    const [plateFilter, setPlateFilter] = useState(filters.plate ?? '');

    const isFocused = useIsFocused();
    const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ['searchLprDetections', filters],
        enabled: isFocused,
        queryFn: ({ pageParam = 0 }) => {
            return licensePlateRecognitionService.getDetections({
                ...filters,
                limit,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length < limit ? undefined : allPages.length,
    });

    const occurrences = useMemo(
        () => data?.pages.flat() ?? [],
        [data]
    );

    const { data: countData, isFetching: countIsLoading } = useQuery({
        queryKey: ['countLprDetections', filters],
        queryFn: () => licensePlateRecognitionService.countDetections(filters),
        enabled: isFocused,
    });

    function handleFilters() {
        setFilters({
            ...filters,
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
        });

        navigation.setParams({
            plate: plateFilter !== '' ? plateFilter.toUpperCase() : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return <Container>
        <Header
            title={translate('searchLpr')}
        />
        <Card>
            <DataTableHeader
                title={`${!countIsLoading && countData ? countData.count + ' ' : ''} ${translate('registeredDetections')}`}
                zIndex={2}
                filter={<LprSearchFilter filters={filters} setFilters={setFilters} plateFilter={plateFilter} setPlateFilter={setPlateFilter} />}
                filterInput={<FilterInput
                    value={plateFilter}
                    onChange={setPlateFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<EventList
                    events={occurrences}
                    navigation={navigation}
                    loadMore={() => {
                        if (!hasNextPage) {
                            return;
                        }
                        if (isFetchingNextPage) {
                            return;
                        }
                        fetchNextPage();
                    }}
                    isLoading={isLoading}
                    page='search'
                />}
            />
        </Card>
    </Container>;

}
